import React, { useState } from "react";
import {
 PaymentElement,
 useStripe,
 useElements,
} from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/Button";
import { Alert } from "./ui/Alert";
import { Stack } from "./ui/Stack";
import { Price } from "./ui/Price";

interface StripePaymentFormProps {
 clientSecret: string;
 amount: number;
 currency?: string;
 onSuccess: (paymentIntentId: string) => void;
 onError: (error: string) => void;
}

export const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
 clientSecret,
 amount,
 currency = "USD",
 onSuccess,
 onError,
}) => {
 const { t } = useTranslation();
 const stripe = useStripe();
 const elements = useElements();
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState<string | null>(null);

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!stripe || !elements) {
   return;
  }

  setLoading(true);
  setError(null);

  try {
   const { error: submitError } = await elements.submit();
   if (submitError) {
    const errorMessage = submitError.message || t("checkout.errorOccurred");
    setError(errorMessage);
    onError(errorMessage);
    setLoading(false);
    return;
   }
   const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
    elements,
    clientSecret,
    confirmParams: {
     return_url: `${window.location.origin}/checkout/success`,
    },
    redirect: "if_required",
   });

   if (confirmError) {
    const errorMessage = confirmError.message || t("checkout.paymentFailed");
    setError(errorMessage);
    onError(errorMessage);
   } else if (paymentIntent) {
    // For manual capture, payment is authorized when status is "requires_capture"
    // For immediate capture (backward compatibility), status is "succeeded"
    if (paymentIntent.status === "requires_capture" || paymentIntent.status === "succeeded") {
     onSuccess(paymentIntent.id);
    } else {
     const errorMessage = t("checkout.unexpectedPaymentStatus", { status: paymentIntent.status });
     setError(errorMessage);
     onError(errorMessage);
    }
   }
  } catch (err: any) {
   const errorMessage = err.message || t("checkout.errorOccurred");
   setError(errorMessage);
   onError(errorMessage);
  } finally {
   setLoading(false);
  }
 };

 return (
  <form onSubmit={handleSubmit}>
   <Stack spacing="md">
    {error && <Alert variant="error">{error}</Alert>}
    
    <div className="p-4 bg-gray-50 border border-gray-200 ">
     <div className="flex justify-between items-center mb-4">
      <span className="text-sm font-medium text-gray-700">Total Amount:</span>
      <Price amount={amount} currency={currency} size="lg" />
     </div>
     <div className="pt-4 border-t border-gray-200">
      <PaymentElement />
     </div>
    </div>

    <Button type="submit" loading={loading} disabled={!stripe || !elements || loading}>
     {t("checkout.completePayment")}
    </Button>
   </Stack>
  </form>
 );
};

